import { useEffect, useState } from "react";
import {
  Users,
  Receipt,
  DollarSign,
  TrendingUp,
  Activity as ActivityIcon,
  CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";

const Metric = ({ icon: Icon, label, value, accent }) => (
  <div className="hp-glass rounded-2xl p-5">
    <div
      className="h-10 w-10 rounded-xl grid place-items-center mb-3"
      style={{ background: `${accent}22`, color: accent }}
    >
      <Icon size={20} />
    </div>
    <p className="text-2xl font-extrabold text-white">{value}</p>
    <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get("/admin/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load admin data"));
  }, []);

  if (err)
    return <p className="text-center text-muted-foreground py-20">{err}</p>;

  if (!data)
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-2xl bg-white/5" />
        ))}
      </div>
    );

  const m = data.metrics || {};
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-extrabold text-white">Admin</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Platform overview & revenue
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Metric icon={Users} label="Total users" value={m.total_users ?? 0} accent="#c084fc" />
        <Metric icon={Receipt} label="Total bills" value={m.total_bills ?? 0} accent="#a855f7" />
        <Metric icon={ActivityIcon} label="Active bills" value={m.active_bills ?? 0} accent="#7c3aed" />
        <Metric icon={CheckCircle2} label="Funded bills" value={m.funded_bills ?? 0} accent="#34d399" />
        <Metric
          icon={DollarSign}
          label="Collected"
          value={money(m.total_funding_collected)}
          accent="#34d399"
        />
        <Metric
          icon={TrendingUp}
          label="Platform revenue"
          value={money(m.total_platform_revenue)}
          accent="#c084fc"
        />
      </div>

      <div className="hp-glass rounded-3xl p-6">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-lg font-bold text-white">{m.confirmed_transactions ?? 0}</p>
            <p className="text-xs text-muted-foreground">Confirmed txns</p>
          </div>
          <div>
            <p className="text-lg font-bold text-white">{m.pending_transactions ?? 0}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
          <div>
            <p className="text-lg font-bold text-white">
              {m.card_transactions ?? 0}/{m.bank_transactions ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">Card / Bank</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-bold text-white mb-3">Recent activity</h2>
        <div className="hp-glass rounded-3xl divide-y divide-white/5">
          {(data.recent_activity || []).length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">
              No activity yet
            </p>
          ) : (
            (data.recent_activity || []).map((a, i) => (
              <div
                key={a.id || `${a.type || "act"}-${i}`}
                className="p-4 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">
                    {a.message || a.type}
                  </p>
                  <p className="text-xs text-muted-foreground">{a.type}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
